/**
 * Unified TypeScript types for streaming architecture
 */

// Unified Edge Stream Message structure (stream/edge topic)
export interface EdgeStreamMessage {
    uuid?: string;
    last_uuid?: string | null;
    /** Publisher id; each publisher runs its own uuid/last_uuid chain. */
    uniqueId?: string;
    timestamp?: string | number;
    scope:
        | "incident"
        | "location"
        | "notification"
        | "system"
        | "user"
        | "device"
        | "beaconPlacementLocation"
        /** Event log rows in deltas.events; inserted by the event log, not applyDelta. */
        | "event"
        /**
         * Full-state broadcast this client does not consume. The stream
         * handler drops these messages entirely — see
         * handleEdgeStreamMessage. Deliberately absent from VenueDelta's
         * scope union: applyDelta must never receive it.
         */
        | "snapshot";
    method: "POST" | "PUT" | "PATCH" | "DELETE";
    action?: string;
    deltas?: {
        incidents?: Array<any>;
        markers?: Array<any>;
        safeZones?: Array<any>;
        users?: Array<any>;
        devices?: Array<any>;
        comments?: Array<any>;
        incidentSafeZones?: Array<any>;
        endUsersAssisting?: Array<any>;
        incidentDuplicates?: Array<any>;
        beaconPlacementLocations?: Array<any>;
        BeaconPlacementLocations?: Array<any>;
        events?: EventLogEntry[];
    };
    toast?: {
        show: boolean;
        severity: "success" | "info" | "warn" | "error";
        summary: string;
        detail?: string;
        life?: number;
    } & Record<string, any>;
}

/** One event log row, as returned by GET /events. */
export interface EventLogEntry {
    id: number;
    incidentId?: number | null;
    venueId?: number | null;
    label?: string;
    eventLogType?: string;
    createdBy?: number | null;
    createdByName?: string;
    createdAt?: string;
    // JSON-encoded key/value pairs, present on some event types.
    metadata?: string | null;
    [key: string]: unknown;
}

export type StreamMessage = EdgeStreamMessage;

// Stream state
export interface StreamState {
    isConnected: boolean;
    isConnecting: boolean;
    lastError: string | null;
}

// Topic mapping
export const STREAM_TOPICS = {
    edge: "stream/edge", // New unified delta stream
} as const;

export type StreamTopic = keyof typeof STREAM_TOPICS;
export type StreamTopicValue = (typeof STREAM_TOPICS)[StreamTopic];
